import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined'
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined'
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined'
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined'
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined'
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined'
import SafetyDividerOutlinedIcon from '@mui/icons-material/SafetyDividerOutlined'
import {
  Avatar,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Typography
} from '@mui/material'
import { blueGrey } from '@mui/material/colors'
import { t } from 'i18next'
import { MouseEvent, useMemo, useState } from 'react'
import { Link, NavLink, useParams } from 'react-router'
import cofinanciadoUeVertical from '../assets/images/cofinanciado-ue-vertical.png'
import comunidadDeMadrid from '../assets/images/comunidad-de-madrid.png'
import { useCompany } from '../hooks/useQueries/useCompanies'
import { useWorkspace } from '../hooks/useQueries/useWorkspaces'
import { getAvatarName } from '../utils/user.utils'

const iconStyles = {
  color: 'custom.semiTransparentWhite'
}

export default function SidebarWorkspace() {
  const params = useParams()
  const companyId = params.companyId!
  const workspaceId = params.workspaceId!
  const { data: company } = useCompany(companyId)
  const { data: workspace } = useWorkspace(companyId, workspaceId)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const navigationItems = useMemo(
    () => [
      {
        title: 'workspace.sidebar.digital_twin',
        icon: <SafetyDividerOutlinedIcon sx={iconStyles} />,
        path: `/companies/${companyId}/workspaces/${workspaceId}/digital-twin`
      },
      {
        title: 'workspace.sidebar.materials',
        icon: <CategoryOutlinedIcon sx={iconStyles} />,
        path: `/companies/${companyId}/workspaces/${workspaceId}/asset-definitions`
      },
      {
        title: 'workspace.sidebar.material_reception',
        icon: <LayersOutlinedIcon sx={iconStyles} />,
        path: `/companies/${companyId}/workspaces/${workspaceId}/assets`
      },
      {
        title: 'workspace.sidebar.inspections',
        icon: <ManageSearchOutlinedIcon sx={iconStyles} />,
        path: `/companies/${companyId}/workspaces/${workspaceId}/inspections`
      },
      {
        title: 'workspace.sidebar.construction_logs',
        icon: <MenuBookOutlinedIcon sx={iconStyles} />,
        path: `/companies/${companyId}/workspaces/${workspaceId}/construction-log`
      }
    ],
    [companyId, workspaceId]
  )

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <Box
      sx={{
        px: '1rem',
        backgroundColor: 'background.default',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box sx={{ py: '1rem' }}>
        {workspace ? (
          <Stack spacing='1rem'>
            <Box sx={{ height: '12rem', width: '100%' }}>
              {workspace.logoUri ? (
                <Box
                  component='img'
                  src={workspace.logoUri}
                  alt={workspace.name}
                  sx={{
                    height: '100%',
                    width: '100%',
                    objectFit: 'cover',
                    borderRadius: '.25rem'
                  }}
                />
              ) : (
                <Avatar
                  variant='square'
                  sx={{
                    height: '100%',
                    width: '100%',
                    fontSize: '2rem'
                  }}
                >
                  {getAvatarName(workspace.name!, '')}
                </Avatar>
              )}
            </Box>

            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='center'
            >
              <Stack direction='column'>
                <Typography variant='body1' sx={{ color: 'text.primary' }}>
                  {workspace.name}
                </Typography>
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                  {company?.name}
                </Typography>
              </Stack>
              <IconButton
                onClick={handleClick}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.12)'
                }}
              >
                <MoreHorizOutlinedIcon
                  sx={{ color: 'custom.semiTransparentWhite' }}
                />
              </IconButton>
            </Stack>
          </Stack>
        ) : (
          <Stack sx={{ gap: '1rem' }}>
            <Skeleton
              variant='rounded'
              sx={{ width: '100%', height: '12.5rem', bgcolor: blueGrey[100] }}
            />
            <Stack direction='column'>
              <Skeleton
                variant='text'
                sx={{ width: '12rem', height: '2rem', bgcolor: blueGrey[100] }}
              />
              <Skeleton
                variant='text'
                sx={{ width: '6rem', height: '2rem', bgcolor: blueGrey[100] }}
              />
            </Stack>
          </Stack>
        )}
      </Box>

      <List sx={{ flex: 1 }}>
        {navigationItems.map(({ title, icon, path }) => (
          <NavLink key={title} to={path} style={{ textDecoration: 'none' }}>
            {({ isActive }) => (
              <ListItem disablePadding>
                <ListItemButton
                  sx={{
                    backgroundColor: isActive
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 'auto', mr: '1rem' }}>
                    {icon}
                  </ListItemIcon>
                  <ListItemText
                    sx={{ color: 'text.primary' }}
                    primary={t(title)}
                  />
                </ListItemButton>
              </ListItem>
            )}
          </NavLink>
        ))}
      </List>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              backgroundColor: '#2E2E2E',
              borderRadius: '.25rem',
              overflow: 'visible',
              width: '13.75rem',
              filter: 'drop-shadow(0px 0.125rem 0.5rem rgba(0,0,0,0.32))'
            }
          }
        }}
      >
        <MenuItem
          component={Link}
          to={`/companies/${companyId}/workspaces/${workspaceId}/edit`}
        >
          <ListItemIcon sx={iconStyles}>
            <CreateOutlinedIcon fontSize='small' />
          </ListItemIcon>
          {t('workspace.sidebar.profile')}
        </MenuItem>
      </Menu>

      <Stack
        direction='row'
        alignItems='center'
        justifyContent='center'
        spacing='1rem'
        sx={{ py: '1rem' }}
      >
        <img
          alt='Comunidad de Madrid'
          src={comunidadDeMadrid}
          style={{ height: '2.5rem' }}
        />
        <img
          alt='Cofinanciado por la Unión Europea'
          src={cofinanciadoUeVertical}
          style={{ height: '4rem', maxWidth: '50%', objectFit: 'contain' }}
        />
      </Stack>
    </Box>
  )
}
